/**
 * Phase 18.3 — "Almost there…" next-step panel (web).
 *
 * Mirror of the mobile NextStepPanel: reactive checklist derived from
 * live state (estimate / selections / anchors / photos / snapshot).
 * Walks the customer through identify → photo → materials → measurement
 * → estimate. Only the *next* missing step gets a CTA so the customer
 * sees the natural next action rather than a wall of buttons.
 */

import type {
  Estimate,
  EstimateSnapshot,
  MaterialSelection,
  Photo,
  ScaleAnchor,
} from "@starz/shared/api/types.gen";

interface Props {
  estimate: Estimate;
  selections: MaterialSelection[];
  anchors: ScaleAnchor[];
  photos: Photo[];
  snapshot: EstimateSnapshot | null;
  onShowOptions: () => void;
  onAddMeasurement: () => void;
  onAddPhoto: () => void;
  onCreateEstimate: () => void;
  disabled?: boolean;
}

type StepKey = "identify" | "photo" | "select" | "measure" | "compute";

interface Step {
  key: StepKey;
  label: string;
  done: boolean;
  cta?: { text: string; onClick: () => void };
}

function buildSteps({
  estimate,
  selections,
  anchors,
  photos,
  snapshot,
  onShowOptions,
  onAddMeasurement,
  onAddPhoto,
  onCreateEstimate,
}: Omit<Props, "disabled">): Step[] {
  return [
    {
      key: "identify",
      label: estimate.room_label || "Identify the project",
      done: !!estimate.room_type,
    },
    {
      key: "photo",
      label:
        photos.length === 0
          ? "Add a photo of the space"
          : `${photos.length} photo${photos.length === 1 ? "" : "s"} added`,
      done: photos.length > 0,
      cta: photos.length === 0 ? { text: "Add a photo", onClick: onAddPhoto } : undefined,
    },
    {
      key: "select",
      label:
        selections.length === 0
          ? "Pick at least one material"
          : `${selections.length} material${selections.length === 1 ? "" : "s"} picked`,
      done: selections.length > 0,
      cta:
        selections.length === 0
          ? { text: "Show me material options", onClick: onShowOptions }
          : undefined,
    },
    {
      key: "measure",
      label:
        anchors.length === 0
          ? "Add a measurement"
          : `${anchors.length} measurement${anchors.length === 1 ? "" : "s"} recorded`,
      done: anchors.length > 0,
      cta:
        anchors.length === 0
          ? { text: "Add a measurement", onClick: onAddMeasurement }
          : undefined,
    },
    {
      key: "compute",
      label: snapshot ? "Estimate generated" : "Generate the estimate",
      done: snapshot !== null,
      cta:
        snapshot === null
          ? { text: "Get my estimate", onClick: onCreateEstimate }
          : undefined,
    },
  ];
}

export function NextStepPanel(props: Props) {
  const steps = buildSteps(props);
  const allDone = steps.every((s) => s.done);
  if (allDone) return null;
  const nextIdx = steps.findIndex((s) => !s.done);

  return (
    <section className="rounded-xl border border-starz-gold/30 bg-starz-cream p-5">
      <h3 className="font-serif text-lg text-starz-navy">Almost there…</h3>
      <p className="mt-1 text-sm text-warm-gray">
        A few more pieces and Stella can put your estimate together.
      </p>
      <ol className="mt-4 space-y-2">
        {steps.map((s, idx) => {
          const isNext = idx === nextIdx;
          return (
            <li key={s.key} className="flex items-start gap-3">
              <span
                className={`w-4 text-center font-mono text-sm ${
                  s.done
                    ? "text-emerald-700"
                    : isNext
                      ? "font-bold text-starz-navy"
                      : "text-warm-gray"
                }`}
              >
                {s.done ? "✓" : isNext ? "→" : "○"}
              </span>
              <div className="flex-1">
                <p
                  className={`text-sm ${
                    s.done
                      ? "text-starz-charcoal line-through opacity-60"
                      : isNext
                        ? "font-semibold text-starz-navy"
                        : "text-warm-gray"
                  }`}
                >
                  {s.label}
                </p>
                {isNext && s.cta ? (
                  <button
                    type="button"
                    disabled={props.disabled}
                    onClick={s.cta.onClick}
                    className="mt-2 rounded-full bg-starz-navy px-4 py-2 text-sm font-semibold text-starz-off-white disabled:opacity-50"
                  >
                    {s.cta.text}
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
